import type { ComponentType } from "react";
import type { PageMeta, SectionDefinition } from "./types";
import { collectSectionQueries } from "./define-section";
import { buildHeadConfig } from "./build-head";
import { publicOrigin } from "./config";

interface SSRPageOptions<C extends ComponentType<any>> {
  component: C;
  sections?: ReadonlyArray<SectionDefinition<any>>;
  meta: PageMeta;
}

/**
 * Wraps a route definition so it opts into SSR, prefetches its sections'
 * queries, and exposes per-page meta to both server and client renderers.
 */
export function ssrPage<C extends ComponentType<any>>(opts: SSRPageOptions<C>) {
  return {
    component: opts.component,
    staticData: { ssr: true as const, meta: opts.meta },
    head: ({ match }: { match: { pathname: string } }) => {
      // Client-side head update on navigation. In the browser the canonical
      // origin is always window.location.origin; on the server we fall back
      // to publicOrigin() which reads PUBLIC_URL. This avoids requiring
      // PUBLIC_URL to be inlined into the client bundle.
      const origin =
        typeof window !== "undefined" ? window.location.origin : publicOrigin();
      const url = new URL(match.pathname, origin);
      // Resolve translations via the runtime i18next instance on the client.
      // On server-side rendering, buildHeadString() is used directly with the
      // per-request i18n instance, so this code path is client-only.
      // Use lazy require() instead of a top-level import because define-page
      // is imported by route files that also run server-side; importing
      // src/i18n.ts at module level would execute its init() on the server.
      let t: (k: string) => string = (k) => k;
      if (typeof window !== "undefined") {
        try {
          // eslint-disable-next-line @typescript-eslint/no-require-imports
          const i18n = require("@/i18n").default;
          t = (k: string) => i18n.t(k);
        } catch {
          // Fallback: keep raw key.
        }
      }
      return buildHeadConfig(opts.meta, {
        url,
        locale:
          (typeof window !== "undefined" && window.__SSR__?.locale) || "bs",
        t,
      });
    },
    loader: opts.sections
      ? collectSectionQueries(opts.sections)
      : undefined,
  };
}
